import type {
  ManageGroup,
  GroupFilters,
  GroupStatus,
  GroupStatusFilter,
  GroupRoleSection,
  GroupRoleTone,
} from '../types/groups.types';
import {
  getOrderedRoleKeys,
  getRoleToneByKey,
  isKnownRoleKey,
} from './roles.utils';

export function generateGroupImportKeyPreview(displayName: string): string {
  return displayName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function getGroupStatus(archivedAt: string | null): GroupStatus {
  return archivedAt ? 'archived' : 'active';
}

export function formatGroupStatus(archivedAt: string | null): string {
  return getGroupStatus(archivedAt) === 'archived' ? 'Archived' : 'Active';
}

export function filterGroups(
  groups: ManageGroup[],
  { searchQuery, status = 'active' }: GroupFilters
): ManageGroup[] {
  const query = searchQuery.trim().toLowerCase();

  return groups.filter((group) => {
    const groupStatus: GroupStatusFilter = group.archived_at
      ? 'archived'
      : 'active';
    if (status !== 'all' && groupStatus !== status) {
      return false;
    }

    if (!query) {
      return true;
    }

    const searchableText = [
      group.display_name,
      group.import_key,
      group.description ?? '',
    ]
      .join(' ')
      .toLowerCase();

    return searchableText.includes(query);
  });
}

export function groupGroupsByParentRole(
  groups: ManageGroup[],
  roleKeys = getOrderedRoleKeys()
): GroupRoleSection[] {
  return roleKeys
    .map((parentRole) => {
      const roleGroups = groups.filter(
        (group) => group.parent_role === parentRole
      );

      return {
        parentRole,
        groups: roleGroups,
        groupCount: roleGroups.length,
        memberCount: roleGroups.reduce(
          (total, group) => total + Number(group.member_count),
          0
        ),
      };
    })
    .filter((section) => section.groupCount > 0);
}

export function getGroupRoleTone(
  parentRole: string | null | undefined
): GroupRoleTone {
  return getRoleToneByKey(parentRole);
}

export function formatGroupSectionSummary(
  groupCount: number,
  memberCount: number
): string {
  const groupLabel = groupCount === 1 ? 'group' : 'groups';
  const memberLabel = memberCount === 1 ? 'member' : 'members';

  return `${groupCount} ${groupLabel} · ${memberCount} ${memberLabel}`;
}

export function formatGroupCreatedContext(
  createdAt: string,
  memberCount: number
): string {
  const date = new Date(createdAt);
  const createdDate = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const memberLabel = memberCount === 1 ? 'member' : 'members';

  return `Created ${createdDate} · ${memberCount} ${memberLabel}`;
}

export function isUserRole(value: string): boolean {
  return isKnownRoleKey(value);
}
